import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/Home';
import SpotifyCallback from './pages/SpotifyCallback';
import CallbackGeneric from './pages/CallbackGeneric';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CookieConsent } from './components/CookieConsent';
import './App.css';

// Import verification utility for production
if (process.env.NODE_ENV === 'production') {
  import('./utils/verifySupabaseTracking');
}



interface AuthState {
  isAuthenticated: boolean;
  platform?: 'spotify';
  token?: string;
}

function App() {
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false
  });
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth tokens on mount
    const checkAuth = async () => {
      const spotifyTokensStr = localStorage.getItem('spotify_tokens');
      let spotifyToken = null;
      
      if (spotifyTokensStr) {
        try {
          const tokens = JSON.parse(spotifyTokensStr);
          if (tokens.access_token && tokens.expires_at > Date.now()) {
            spotifyToken = tokens.access_token;
          }
        } catch (e) {
          console.error('Failed to parse Spotify tokens');
        }
      }
      
      if (spotifyToken) {
        setAuth({
          isAuthenticated: true,
          platform: 'spotify',
          token: spotifyToken
        });
        setAuthLoading(false);
        return;
      }


      // If no valid tokens found, user is not authenticated
      setAuth({
        isAuthenticated: false
      });
      
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  // MVP: Disable background preloading to avoid auth-dependent API calls
  // This prevents errors when no user authentication is available

  const handleLogin = (token: string, platform: 'spotify') => {
    setAuth({
      isAuthenticated: true,
      platform,
      token
    });
  };

  const handleLogout = async () => {
    try {
      // Clean up Spotify tokens
      localStorage.removeItem('spotify_access_token');
      localStorage.removeItem('spotify_refresh_token');
      
      
      setAuth({
        isAuthenticated: false
      });
      
      console.log('✅ User logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Still update auth state even if logout fails
      setAuth({
        isAuthenticated: false
      });
    }
  };

  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="min-h-screen">
          <div className="App">
            <main>
              {authLoading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100vh',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  <div>Loading...</div>
                </div>
              ) : (
                <Routes>
                  <Route 
                    path="/" 
                    element={<Home auth={auth} onLogout={handleLogout} />} 
                  />
                  <Route 
                    path="/sets" 
                    element={<Home auth={auth} onLogout={handleLogout} />} 
                  />
                  <Route path="/callback" element={<CallbackGeneric />} />
                  <Route path="/spotify/callback" element={<SpotifyCallback />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                </Routes>
              )}
            </main>
          </div>
        </div>
      </Router>
      <CookieConsent />
    </ErrorBoundary>
  );
}

export default App;
